import React, { useState, useEffect } from 'react';
import {
  Modal,
  Tabs,
  Form,
  Input,
  Table,
  Checkbox,
  Button,
  Space,
  message,
  Typography,
  Tag,
  Descriptions,
  Alert,
  Collapse,
  Divider,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import { apiClient } from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../services/api/config';
import type { KrybotAgentRun, KrybotApproval } from '../../../types/krybot';
import './PrimeAgentIntegration.css';

const { TextArea } = Input;
const { Text, Title } = Typography;

interface ArtifactReviewDialogProps {
  open: boolean;
  run: KrybotAgentRun;
  approval: KrybotApproval;
  artifactId: string;
  focusAreas?: string[];  // Which focus areas this approval is for: 'properties', 'states', 'transitions', 'processes'
  onClose: () => void;
  onApprovalDecision?: () => void;
}

interface ProposedChanges {
  artifact?: {
    name?: string;
    description?: string;
    artifact_type?: string;
  };
  states?: Array<{
    id?: string;
    name: string;
    state_type?: string;
    description?: string;
    is_initial?: boolean;
    is_final?: boolean;
  }>;
  transitions?: Array<{
    id?: string;
    name: string;
    from_state?: string;
    to_state?: string;
    description?: string;
  }>;
  processes?: Array<{
    id?: string;
    name: string;
    description?: string;
    process_type?: string;
  }>;
  properties?: Record<string, unknown>;
}

export const ArtifactReviewDialog: React.FC<ArtifactReviewDialogProps> = ({
  open,
  run,
  approval,
  artifactId,
  focusAreas = [],
  onClose,
  onApprovalDecision,
}) => {
  const [form] = Form.useForm();
  const [proposedChanges, setProposedChanges] = useState<ProposedChanges>({});
  const [originalArtifact, setOriginalArtifact] = useState<any>(null);
  const [loadingOriginal, setLoadingOriginal] = useState(false);
  const [selectedStates, setSelectedStates] = useState<Set<string>>(new Set());
  const [selectedTransitions, setSelectedTransitions] = useState<Set<string>>(new Set());
  const [selectedProcesses, setSelectedProcesses] = useState<Set<string>>(new Set());
  const [approveArtifact, setApproveArtifact] = useState(false);
  const [approveProperties, setApproveProperties] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      extractProposedChanges();
      loadOriginalArtifact();
    }
  }, [open, run, approval, artifactId]);

  const extractProposedChanges = () => {
    try {
      // Try to get proposed changes from approval first
      let changes: ProposedChanges = {};
      
      console.log('[ArtifactReviewDialog] Extracting proposed changes', {
        hasApproval: !!approval,
        approvalProposedChanges: approval?.proposed_changes,
        hasOutputPayload: !!run.output_payload,
        outputPayload: run.output_payload,
      });
      
      // Start with approval.proposed_changes if available, but also check output_payload for missing data
      let hasChangesFromApproval = false;
      if (approval?.proposed_changes && typeof approval.proposed_changes === 'object') {
        changes = approval.proposed_changes as ProposedChanges;
        hasChangesFromApproval = true;
        console.log('[ArtifactReviewDialog] Using approval.proposed_changes:', {
          hasArtifact: !!(changes as any).artifact,
          hasProperties: Array.isArray((changes as any).properties) && (changes as any).properties.length > 0,
          propertiesCount: Array.isArray((changes as any).properties) ? (changes as any).properties.length : 0,
          hasStates: Array.isArray((changes as any).states) && (changes as any).states.length > 0,
          hasTransitions: Array.isArray((changes as any).transitions) && (changes as any).transitions.length > 0,
          hasProcesses: Array.isArray((changes as any).processes) && (changes as any).processes.length > 0,
        });
        
        // If properties are in array format from proposed_changes, convert to object format for display
        if (Array.isArray((changes as any).properties) && (changes as any).properties.length > 0) {
          const propsArray = (changes as any).properties as Array<any>;
          const propsObj: Record<string, unknown> = {};
          propsArray.forEach((prop: any) => {
            if (prop.property_key) {
              propsObj[prop.property_key] = { ...prop, _updated: true };
            }
          });
          changes.properties = propsObj;
          console.log('[ArtifactReviewDialog] Converted properties array to object:', Object.keys(propsObj));
        }
      }
      
      // Always check output_payload as fallback/supplement, especially for properties
      // Even if we have approval.proposed_changes, properties might be missing and need to be extracted from raw payload
      let propertiesFromPayload: Record<string, unknown> | null = null;
      if (run.output_payload) {
        // Parse from output_payload
        let payload: any = run.output_payload;
        if (typeof payload === 'string') {
          const payloadString = payload;
          try {
            payload = JSON.parse(payloadString);
          } catch (e) {
            console.error('[ArtifactReviewDialog] Failed to parse output_payload as JSON:', e);
            // Try to extract JSON from string if it contains text + JSON
            try {
              const jsonMatch = payloadString.match(/\{[\s\S]*\}/);
              if (jsonMatch) {
                payload = JSON.parse(jsonMatch[0]);
                console.log('[ArtifactReviewDialog] Extracted JSON from string response');
              } else {
                payload = { response: payloadString };
              }
            } catch (e2) {
              console.error('[ArtifactReviewDialog] Failed to extract JSON from string:', e2);
              payload = { response: payloadString };
            }
          }
        }
        
        // If payload has a "response" field that's a string, try to parse it
        if (payload.response && typeof payload.response === 'string' && !payload.original_artifact && !payload.updated_artifact) {
          try {
            // Use brace counting to find balanced JSON object
            const responseText = payload.response;
            let braceCount = 0;
            let startIdx = -1;
            
            for (let i = 0; i < responseText.length; i++) {
              if (responseText[i] === '{') {
                if (startIdx === -1) {
                  startIdx = i;
                }
                braceCount++;
              } else if (responseText[i] === '}') {
                braceCount--;
                if (braceCount === 0 && startIdx !== -1) {
                  // Found balanced JSON object
                  try {
                    const jsonStr = responseText.substring(startIdx, i + 1);
                    const extracted = JSON.parse(jsonStr);
                    payload = { ...payload, ...extracted };
                    console.log('[ArtifactReviewDialog] Extracted JSON from response field using brace counting');
                    break;
                  } catch (parseErr) {
                    // Continue searching
                    startIdx = -1;
                    braceCount = 0;
                  }
                }
              }
            }
            
            // If brace counting didn't work, try regex as fallback
            if (startIdx === -1 || braceCount !== 0) {
              const jsonMatch = payload.response.match(/\{[\s\S]*\}/);
              if (jsonMatch) {
                try {
                  const extracted = JSON.parse(jsonMatch[0]);
                  payload = { ...payload, ...extracted };
                  console.log('[ArtifactReviewDialog] Extracted JSON from response field using regex');
                } catch (e) {
                  console.warn('[ArtifactReviewDialog] Could not parse extracted JSON:', e);
                }
              }
            }
          } catch (e) {
            console.warn('[ArtifactReviewDialog] Could not extract JSON from response field:', e);
          }
        }
        
        console.log('[ArtifactReviewDialog] Parsed payload:', payload);
        console.log('[ArtifactReviewDialog] Payload keys:', Object.keys(payload));
        
        // Check if artifact_payload is nested (from Krybot API response structure)
        if (payload.artifact_payload && typeof payload.artifact_payload === 'object') {
          console.log('[ArtifactReviewDialog] Found nested artifact_payload, using that');
          payload = payload.artifact_payload;
        }
        
        // Check for new format with original_* and updated_* sections
        const hasNewFormat = payload.original_artifact || payload.updated_artifact || 
                            payload.original_states || payload.updated_states;
        
        if (hasNewFormat) {
          console.log('[ArtifactReviewDialog] Detected new format with original/updated sections');
          
          // Extract from updated_* sections for proposed changes
          // If updated_* doesn't exist, fall back to original_* (agent may have only returned baseline)
          const statesData = payload.updated_states || payload.original_states || [];
          const transitionsData = payload.updated_transitions || payload.original_transitions || [];
          const processesData = payload.updated_processes || payload.original_processes || [];
          const artifactData = payload.updated_artifact || payload.original_artifact || {};
          
          const statesArray = Array.isArray(statesData) ? statesData : [];
          const transitionsArray = Array.isArray(transitionsData) ? transitionsData : [];
          const processesArray = Array.isArray(processesData) ? processesData : [];
          
          // Extract properties from artifact objects if not in separate arrays
          let propertiesObj: Record<string, unknown> = {};
          const originalProps = payload.original_properties || [];
          const updatedProps = payload.updated_properties || [];
          
          console.log('[ArtifactReviewDialog] Properties extraction:', {
            hasOriginalPropsArray: Array.isArray(originalProps) && originalProps.length > 0,
            hasUpdatedPropsArray: Array.isArray(updatedProps) && updatedProps.length > 0,
            hasOriginalArtifactProps: !!payload.original_artifact?.properties,
            hasUpdatedArtifactProps: !!payload.updated_artifact?.properties,
            originalArtifactPropsCount: Array.isArray(payload.original_artifact?.properties) ? payload.original_artifact.properties.length : 0,
            updatedArtifactPropsCount: Array.isArray(payload.updated_artifact?.properties) ? payload.updated_artifact.properties.length : 0,
          });
          
          // Process arrays first if they exist (they take priority)
          if (Array.isArray(originalProps) && originalProps.length > 0) {
            originalProps.forEach((prop: any) => {
              if (prop.property_key) {
                if (!propertiesObj[prop.property_key]) {
                  propertiesObj[prop.property_key] = {};
                }
                propertiesObj[prop.property_key] = { 
                  ...(propertiesObj[prop.property_key] as object),
                  ...prop, 
                  _original: true 
                };
              }
            });
          }
          
          if (Array.isArray(updatedProps) && updatedProps.length > 0) {
            updatedProps.forEach((prop: any) => {
              if (prop.property_key) {
                if (!propertiesObj[prop.property_key]) {
                  propertiesObj[prop.property_key] = {};
                }
                propertiesObj[prop.property_key] = { 
                  ...(propertiesObj[prop.property_key] as object),
                  ...prop, 
                  _updated: true 
                };
              }
            });
          }
          
          // If arrays are empty, extract from artifact.properties
          // Always check nested locations if arrays don't have data
          const shouldExtractFromNested = (!originalProps || !Array.isArray(originalProps) || originalProps.length === 0);
          if (shouldExtractFromNested && payload.original_artifact?.properties) {
            const props = Array.isArray(payload.original_artifact.properties) ? payload.original_artifact.properties : [];
            console.log('[ArtifactReviewDialog] Extracting properties from original_artifact.properties:', props.length, 'properties found');
            props.forEach((prop: any) => {
              if (prop.property_key) {
                if (!propertiesObj[prop.property_key]) {
                  propertiesObj[prop.property_key] = {};
                }
                const existing = propertiesObj[prop.property_key] as Record<string, unknown>;
                propertiesObj[prop.property_key] = { 
                  ...existing,
                  ...prop, 
                  _original: true,
                  _updated: existing._updated || false,
                };
              }
            });
          }
          
          const shouldExtractUpdatedFromNested = (!updatedProps || !Array.isArray(updatedProps) || updatedProps.length === 0);
          if (shouldExtractUpdatedFromNested && payload.updated_artifact?.properties) {
            const props = Array.isArray(payload.updated_artifact.properties) ? payload.updated_artifact.properties : [];
            console.log('[ArtifactReviewDialog] Extracting properties from updated_artifact.properties:', props.length, 'properties found');
            props.forEach((prop: any) => {
              if (prop.property_key) {
                if (!propertiesObj[prop.property_key]) {
                  propertiesObj[prop.property_key] = {};
                }
                // Merge with existing property, preserving original if it exists
                const existing = propertiesObj[prop.property_key] as Record<string, unknown>;
                propertiesObj[prop.property_key] = { 
                  ...existing,
                  ...prop, 
                  _updated: true,
                  _original: existing._original !== undefined ? existing._original : false,
                };
              }
            });
          }
          
          console.log('[ArtifactReviewDialog] After extraction, properties object has', Object.keys(propertiesObj).length, 'properties:', Object.keys(propertiesObj));
          
          console.log('[ArtifactReviewDialog] Final properties object:', {
            propertyKeys: Object.keys(propertiesObj),
            count: Object.keys(propertiesObj).length,
            sample: Object.keys(propertiesObj).slice(0, 3).reduce((acc, key) => {
              acc[key] = propertiesObj[key];
              return acc;
            }, {} as Record<string, unknown>),
          });
          
          // Store properties extracted from payload
          if (Object.keys(propertiesObj).length > 0) {
            propertiesFromPayload = propertiesObj;
          }
          
          // If we didn't already have changes from approval, use payload extraction
          if (!hasChangesFromApproval) {
            changes = {
              artifact: artifactData && typeof artifactData === 'object' ? artifactData : {},
              states: statesArray,
              transitions: transitionsArray,
              processes: processesArray,
              properties: propertiesObj,
            };
          } else {
            // We have changes from approval - merge properties from payload if missing or if payload has more
            if (!changes.properties || (typeof changes.properties === 'object' && Object.keys(changes.properties).length === 0)) {
              console.log('[ArtifactReviewDialog] No properties in approval, using properties from output_payload');
              changes.properties = propertiesObj;
            } else if (Object.keys(propertiesObj).length > 0) {
              // Merge properties, with payload taking precedence (it has the actual nested data)
              console.log('[ArtifactReviewDialog] Merging properties from output_payload with approval properties');
              const mergedProps = { ...changes.properties, ...propertiesObj };
              changes.properties = mergedProps;
              console.log('[ArtifactReviewDialog] Merged properties:', Object.keys(mergedProps));
            }
          }
          
          console.log('[ArtifactReviewDialog] Extracted changes from new format:', {
            hasUpdated: !!(payload.updated_states || payload.updated_artifact),
            hasOriginal: !!(payload.original_states || payload.original_artifact),
            statesCount: statesArray.length,
            transitionsCount: transitionsArray.length,
            processesCount: processesArray.length,
            propertiesCount: changes.properties ? Object.keys(changes.properties).length : 0,
            artifact: changes.artifact,
          });
        } else {
          // Old format - extract structured data - try multiple possible field names
          const statesData = payload.prime_artifact_states || 
                            payload.states || 
                            payload.prime_artifact_states_list ||
                            payload.state_list ||
                            [];
          const transitionsData = payload.prime_artifact_state_transitions || 
                                   payload.transitions || 
                                   payload.prime_artifact_transitions ||
                                   payload.transition_list ||
                                   [];
          const processesData = payload.prime_processes || 
                               payload.processes || 
                               payload.prime_process_list ||
                               payload.process_list ||
                               [];
          const propertiesData = payload.properties || payload.artifact_properties || {};
          
          // Try to extract artifact info from various locations
          const artifactData = payload.prime_artifact || 
                              payload.artifact || 
                              payload.prime_artifact_data ||
                              {
                                name: payload.name,
                                description: payload.description,
                                artifact_type: payload.artifact_type,
                              };
          
          changes = {
            artifact: artifactData && typeof artifactData === 'object' ? artifactData : {},
            states: Array.isArray(statesData) ? statesData : [],
            transitions: Array.isArray(transitionsData) ? transitionsData : [],
            processes: Array.isArray(processesData) ? processesData : [],
            properties: propertiesData && typeof propertiesData === 'object' ? propertiesData as Record<string, unknown> : {},
          };
          
          console.log('[ArtifactReviewDialog] Extracted changes from old format:', changes);
        }
      }

      // Fix states that have name as id - extract name from id if id is a string name
      if (changes.states) {
        changes.states = changes.states.map((state: any) => {
          // If id exists but is not a UUID (looks like a name) and name is missing, use id as name
          if (state.id && !state.name && typeof state.id === 'string') {
            // Check if id looks like a UUID (contains hyphens and is long)
            const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(state.id);
            if (!isUUID) {
              // id is likely the name, use it as name
              return { ...state, name: state.id };
            }
          }
          return state;
        });
      }

      setProposedChanges(changes);

      // Initialize form with artifact data
      if (changes.artifact) {
        form.setFieldsValue({
          name: changes.artifact.name || '',
          description: changes.artifact.description || '',
        });
      }

      // Select all by default for selective approval
      if (changes.states) {
        setSelectedStates(new Set(changes.states.map((s, idx) => s.id || `state-${idx}`)));
      }
      if (changes.transitions) {
        setSelectedTransitions(new Set(changes.transitions.map((t, idx) => t.id || `transition-${idx}`)));
      }
      if (changes.processes) {
        setSelectedProcesses(new Set(changes.processes.map((p, idx) => p.id || `process-${idx}`)));
      }
    } catch (err) {
      console.error('Failed to extract proposed changes:', err);
      message.error('Failed to parse proposed changes');
    }
  };

  const loadOriginalArtifact = async () => {
    if (!artifactId || loadingOriginal) return;
    
    try {
      setLoadingOriginal(true);
      const response = await apiClient.get(API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(artifactId));
      setOriginalArtifact(response.data);
      console.log('[ArtifactReviewDialog] Loaded original artifact:', response.data);
    } catch (err) {
      console.error('[ArtifactReviewDialog] Failed to load original artifact:', err);
      // Don't show error message - dialog can still work without it
    } finally {
      setLoadingOriginal(false);
    }
  };

  // Helper to check if a tab should be visible based on focus areas
  const shouldShowTab = (tabKey: string): boolean => {
    // If no focus areas specified, show all tabs (backward compatibility)
    if (!focusAreas || focusAreas.length === 0) {
      return true;
    }
    
    // Map tab keys to focus area names
    const tabToFocusArea: Record<string, string> = {
      'details': 'properties',  // Details tab shows artifact + properties
      'properties': 'properties',
      'states': 'states',
      'transitions': 'transitions',
      'processes': 'processes',
    };
    
    const focusArea = tabToFocusArea[tabKey];
    return focusArea ? focusAreas.includes(focusArea) : true;
  };

  // Focus-area-specific approval handler
  const handleApproveFocusArea = async (focusArea: 'properties' | 'states' | 'transitions' | 'processes') => {
    try {
      setSubmitting(true);
      
      // Build selected elements based on focus area
      const selected_elements: any = {};
      
      if (focusArea === 'properties') {
        selected_elements.properties = approveProperties;
        selected_elements.artifact = approveArtifact;
      } else if (focusArea === 'states') {
        selected_elements.states = Array.from(selectedStates);
      } else if (focusArea === 'transitions') {
        selected_elements.transitions = Array.from(selectedTransitions);
      } else if (focusArea === 'processes') {
        selected_elements.processes = Array.from(selectedProcesses);
      }
      
      const payload: any = {
        decision: 'approve',
        notes: `Approved ${focusArea} changes`,
        focus_area: focusArea,
        selected_elements,
      };

      await KrybotService.makeDecision(approval.id, payload);
      message.success(`${focusArea.charAt(0).toUpperCase() + focusArea.slice(1)} approved successfully`);
      onApprovalDecision?.();
      onClose();
    } catch (err: any) {
      console.error('[ArtifactReviewDialog] Approval error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error occurred';
      message.error(`Failed to approve ${focusArea}: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveSelected = async () => {
    try {
      setSubmitting(true);
      
      const payload: any = {
        decision: 'approve',
        notes: 'Approved selected elements',
        selected_elements: {
          artifact: approveArtifact,
          properties: approveProperties,
          states: Array.from(selectedStates),
          transitions: Array.from(selectedTransitions),
          processes: Array.from(selectedProcesses),
        },
      };

      await KrybotService.makeDecision(approval.id, payload);
      message.success('Selected elements approved successfully');
      onApprovalDecision?.();
      onClose();
    } catch (err: any) {
      console.error('[ArtifactReviewDialog] Approval error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error occurred';
      message.error(`Failed to approve: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveAll = async () => {
    try {
      setSubmitting(true);
      
      // Auto-select all available items for "Approve All"
      const allStateKeys = new Set(
        (proposedChanges.states || []).map((s: any, idx: number) => s.id || `state-${idx}`)
      );
      const allTransitionKeys = new Set(
        (proposedChanges.transitions || []).map((t: any, idx: number) => t.id || `transition-${idx}`)
      );
      const allProcessKeys = new Set(
        (proposedChanges.processes || []).map((p: any, idx: number) => p.id || `process-${idx}`)
      );
      
      setSelectedStates(allStateKeys);
      setSelectedTransitions(allTransitionKeys);
      setSelectedProcesses(allProcessKeys);
      setApproveArtifact(true);
      setApproveProperties(true);
      
      // Small delay to let state updates propagate
      await new Promise(resolve => setTimeout(resolve, 50));
      
      const payload: any = {
        decision: 'approve',
        notes: 'Approved all changes',
        selected_elements: {
          artifact: true,
          properties: true,
          states: Array.from(allStateKeys),
          transitions: Array.from(allTransitionKeys),
          processes: Array.from(allProcessKeys),
        },
      };

      await KrybotService.makeDecision(approval.id, payload);
      message.success('All changes approved successfully');
      onApprovalDecision?.();
      onClose();
    } catch (err: any) {
      console.error('[ArtifactReviewDialog] Approval error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error occurred';
      message.error(`Failed to approve: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectNotes.trim()) {
      message.warning('Please provide notes for rejection');
      return;
    }

    try {
      setSubmitting(true);
      
      const payload: any = {
        decision: 'reject',
        notes: rejectNotes,
      };

      await KrybotService.makeDecision(approval.id, payload);
      
      // Send feedback to agent
      if (run.id) {
        try {
          await KrybotService.respondToRun(run.id, rejectNotes);
        } catch (err) {
          console.warn('Failed to send feedback to agent:', err);
        }
      }
      
      message.success('Changes rejected and feedback sent to agent');
      onApprovalDecision?.();
      onClose();
    } catch (err: any) {
      message.error(`Failed to reject: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const stateColumns = [
    {
      title: 'Select',
      key: 'select',
      width: 80,
      render: (_: any, record: any, index: number) => {
        const key = record.id || `state-${index}`;
        return (
          <Checkbox
            checked={selectedStates.has(key)}
            onChange={(e) => {
              const newSet = new Set(selectedStates);
              if (e.target.checked) {
                newSet.add(key);
              } else {
                newSet.delete(key);
              }
              setSelectedStates(newSet);
            }}
          />
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any, index: number) => (
        <Input
          defaultValue={text}
          onChange={(e) => {
            const newStates = [...(proposedChanges.states || [])];
            newStates[index] = { ...record, name: e.target.value };
            setProposedChanges({ ...proposedChanges, states: newStates });
          }}
        />
      ),
    },
    {
      title: 'Type',
      dataIndex: 'state_type',
      key: 'state_type',
      render: (type: string) => <Tag>{type || 'N/A'}</Tag>,
    },
    {
      title: 'Initial',
      dataIndex: 'is_initial',
      key: 'is_initial',
      render: (isInitial: boolean) => (isInitial ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: 'Final',
      dataIndex: 'is_final',
      key: 'is_final',
      render: (isFinal: boolean) => (isFinal ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: any, index: number) => (
        <TextArea
          rows={2}
          defaultValue={text || ''}
          onChange={(e) => {
            const newStates = [...(proposedChanges.states || [])];
            newStates[index] = { ...record, description: e.target.value };
            setProposedChanges({ ...proposedChanges, states: newStates });
          }}
        />
      ),
    },
  ];

  const transitionColumns = [
    {
      title: 'Select',
      key: 'select',
      width: 80,
      render: (_: any, record: any, index: number) => {
        const key = record.id || `transition-${index}`;
        return (
          <Checkbox
            checked={selectedTransitions.has(key)}
            onChange={(e) => {
              const newSet = new Set(selectedTransitions);
              if (e.target.checked) {
                newSet.add(key);
              } else {
                newSet.delete(key);
              }
              setSelectedTransitions(newSet);
            }}
          />
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any, index: number) => (
        <Input
          defaultValue={text}
          onChange={(e) => {
            const newTransitions = [...(proposedChanges.transitions || [])];
            newTransitions[index] = { ...record, name: e.target.value };
            setProposedChanges({ ...proposedChanges, transitions: newTransitions });
          }}
        />
      ),
    },
    {
      title: 'From State',
      dataIndex: 'from_state',
      key: 'from_state',
      render: (fromState: string | { name?: string }) => {
        const name = typeof fromState === 'string' ? fromState : fromState?.name || 'N/A';
        return <Tag>{name}</Tag>;
      },
    },
    {
      title: 'To State',
      dataIndex: 'to_state',
      key: 'to_state',
      render: (toState: string | { name?: string }) => {
        const name = typeof toState === 'string' ? toState : toState?.name || 'N/A';
        return <Tag color="blue">{name}</Tag>;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: any, index: number) => (
        <TextArea
          rows={2}
          defaultValue={text || ''}
          onChange={(e) => {
            const newTransitions = [...(proposedChanges.transitions || [])];
            newTransitions[index] = { ...record, description: e.target.value };
            setProposedChanges({ ...proposedChanges, transitions: newTransitions });
          }}
        />
      ),
    },
  ];

  const processColumns = [
    {
      title: 'Select',
      key: 'select',
      width: 80,
      render: (_: any, record: any, index: number) => {
        const key = record.id || `process-${index}`;
        return (
          <Checkbox
            checked={selectedProcesses.has(key)}
            onChange={(e) => {
              const newSet = new Set(selectedProcesses);
              if (e.target.checked) {
                newSet.add(key);
              } else {
                newSet.delete(key);
              }
              setSelectedProcesses(newSet);
            }}
          />
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any, index: number) => (
        <Input
          defaultValue={text}
          onChange={(e) => {
            const newProcesses = [...(proposedChanges.processes || [])];
            newProcesses[index] = { ...record, name: e.target.value };
            setProposedChanges({ ...proposedChanges, processes: newProcesses });
          }}
        />
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: any, index: number) => (
        <TextArea
          rows={2}
          defaultValue={text || ''}
          onChange={(e) => {
            const newProcesses = [...(proposedChanges.processes || [])];
            newProcesses[index] = { ...record, description: e.target.value };
            setProposedChanges({ ...proposedChanges, processes: newProcesses });
          }}
        />
      ),
    },
    {
      title: 'Type',
      dataIndex: 'process_type',
      key: 'process_type',
      render: (type: string) => type ? <Tag>{type}</Tag> : null,
    },
  ];

  return (
    <Modal
      title="Review Proposed Changes"
      open={open}
      onCancel={onClose}
      width="90vw"
      style={{ top: 20 }}
      footer={
        <Space>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            danger
            icon={<CloseCircleOutlined />}
            onClick={handleReject}
            loading={submitting}
          >
            Reject
          </Button>
          {/* Focus-area-specific approval buttons */}
          {focusAreas && focusAreas.length > 0 ? (
            <>
              {focusAreas.includes('properties') && (
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleApproveFocusArea('properties')}
                  loading={submitting}
                >
                  Approve Properties
                </Button>
              )}
              {focusAreas.includes('states') && (
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleApproveFocusArea('states')}
                  loading={submitting}
                >
                  Approve States
                </Button>
              )}
              {focusAreas.includes('transitions') && (
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleApproveFocusArea('transitions')}
                  loading={submitting}
                >
                  Approve Transitions
                </Button>
              )}
              {focusAreas.includes('processes') && (
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleApproveFocusArea('processes')}
                  loading={submitting}
                >
                  Approve Processes
                </Button>
              )}
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleApproveAll}
                loading={submitting}
              >
                Approve All
              </Button>
            </>
          ) : (
            <>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={handleApproveSelected}
                loading={submitting}
              >
                Approve Selected
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleApproveAll}
                loading={submitting}
              >
                Approve All
              </Button>
            </>
          )}
        </Space>
      }
    >
      <Alert
        message="Review and Edit Proposed Changes"
        description="You can edit the proposed changes before approval. Use checkboxes to selectively approve elements."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Tabs 
        defaultActiveKey="details"
        items={[
          ...(shouldShowTab('details') || shouldShowTab('properties') ? [{
            key: 'details',
            label: 'Details',
            children: (
              <Form form={form} layout="vertical">
                {originalArtifact && (
                  <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="Original Name">
                      {originalArtifact.name || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Original Description">
                      {originalArtifact.description || 'No description'}
                    </Descriptions.Item>
                  </Descriptions>
                )}
                <Form.Item label="Proposed Artifact Name" name="name">
                  <Input placeholder={proposedChanges.artifact?.name || originalArtifact?.name || 'No name provided'} />
                </Form.Item>
                <Form.Item label="Proposed Description" name="description">
                  <TextArea rows={4} placeholder={proposedChanges.artifact?.description || originalArtifact?.description || 'No description provided'} />
                </Form.Item>
                <Form.Item>
                  <Checkbox
                    checked={approveArtifact}
                    onChange={(e) => setApproveArtifact(e.target.checked)}
                  >
                    Approve artifact details changes
                  </Checkbox>
                </Form.Item>
                {proposedChanges.artifact && Object.keys(proposedChanges.artifact).length > 0 && (
                  <Descriptions column={1} bordered size="small" style={{ marginTop: 16 }}>
                    {proposedChanges.artifact.name && (
                      <Descriptions.Item label="Name">
                        {proposedChanges.artifact.name}
                      </Descriptions.Item>
                    )}
                    {proposedChanges.artifact.artifact_type && (
                      <Descriptions.Item label="Artifact Type">
                        {typeof proposedChanges.artifact.artifact_type === 'string'
                          ? proposedChanges.artifact.artifact_type
                          : (proposedChanges.artifact.artifact_type as any).name || 'N/A'}
                      </Descriptions.Item>
                    )}
                    {proposedChanges.artifact.description && (
                      <Descriptions.Item label="Description">
                        {proposedChanges.artifact.description}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                )}
                {(!proposedChanges.artifact || Object.keys(proposedChanges.artifact).length === 0) && (
                  <Alert
                    message="No artifact data found"
                    description="The agent response does not contain artifact details. Check the raw JSON below."
                    type="warning"
                    style={{ marginTop: 16 }}
                  />
                )}
              </Form>
            ),
          }] : []),
          ...(shouldShowTab('properties') ? [{
            key: 'properties',
            label: `Properties (${proposedChanges.properties ? Object.keys(proposedChanges.properties).length : 0})`,
            children: (
              <>
                <Form.Item>
                  <Checkbox
                    checked={approveProperties}
                    onChange={(e) => setApproveProperties(e.target.checked)}
                  >
                    Approve properties changes
                  </Checkbox>
                </Form.Item>
                {proposedChanges.properties && Object.keys(proposedChanges.properties).length > 0 ? (
                  <Table
                    dataSource={Object.entries(proposedChanges.properties).map(([key, value]: [string, any]) => ({
                      key,
                      property_key: key,
                      property_type: value?.property_type || 'unknown',
                      description: value?.description || '',
                      is_new: value?._updated && !value?._original,
                      is_modified: value?._updated && value?._original,
                      ...value,
                    }))}
                    columns={[
                      {
                        title: 'Property Key',
                        dataIndex: 'property_key',
                        key: 'property_key',
                        render: (text: string) => <Text strong>{text}</Text>,
                      },
                      {
                        title: 'Type',
                        dataIndex: 'property_type',
                        key: 'property_type',
                        render: (type: string) => type ? <Tag>{type}</Tag> : <Text type="secondary">-</Text>,
                      },
                      {
                        title: 'Description',
                        dataIndex: 'description',
                        key: 'description',
                        render: (desc: string) => desc || <Text type="secondary" italic>No description</Text>,
                      },
                      {
                        title: 'Status',
                        key: 'status',
                        render: (_: unknown, record: any) => {
                          if (record.is_new) {
                            return <Tag color="green">New</Tag>;
                          }
                          if (record.is_modified) {
                            return <Tag color="blue">Modified</Tag>;
                          }
                          return <Tag color="default">Existing</Tag>;
                        },
                      },
                    ]}
                    pagination={false}
                    size="small"
                    style={{ marginTop: 16 }}
                  />
                ) : (
                  <Alert
                    message="No properties found"
                    description="The agent response does not contain properties. Check the raw JSON below."
                    type="info"
                    style={{ marginTop: 16 }}
                  />
                )}
              </>
            ),
          }] : []),
          ...(shouldShowTab('states') ? [{
            key: 'states',
            label: `States (${proposedChanges.states?.length || 0})`,
            children: proposedChanges.states && proposedChanges.states.length > 0 ? (
              <Table
                dataSource={proposedChanges.states.map((s, idx) => ({ ...s, key: s.id || `state-${idx}` }))}
                columns={stateColumns}
                pagination={false}
                size="small"
              />
            ) : (
              <Text type="secondary">No states proposed</Text>
            ),
          }] : []),
          ...(shouldShowTab('transitions') ? [{
            key: 'transitions',
            label: `Transitions (${proposedChanges.transitions?.length || 0})`,
            children: proposedChanges.transitions && proposedChanges.transitions.length > 0 ? (
              <Table
                dataSource={proposedChanges.transitions.map((t, idx) => ({ ...t, key: t.id || `transition-${idx}` }))}
                columns={transitionColumns}
                pagination={false}
                size="small"
              />
            ) : (
              <Text type="secondary">No transitions proposed</Text>
            ),
          }] : []),
          ...(shouldShowTab('processes') ? [{
            key: 'processes',
            label: `Processes (${proposedChanges.processes?.length || 0})`,
            children: proposedChanges.processes && proposedChanges.processes.length > 0 ? (
              <Table
                dataSource={proposedChanges.processes.map((p, idx) => ({ ...p, key: p.id || `process-${idx}` }))}
                columns={processColumns}
                pagination={false}
                size="small"
              />
            ) : (
              <Text type="secondary">No processes proposed</Text>
            ),
          }] : []),
        ].filter(Boolean)}
      />

      <Divider />

      <Form.Item label="Rejection Notes (if rejecting)">
        <TextArea
          rows={3}
          placeholder="Provide feedback for the agent..."
          value={rejectNotes}
          onChange={(e) => setRejectNotes(e.target.value)}
        />
      </Form.Item>

      <Divider />
      
      <Collapse
        items={[
          {
            key: 'debug',
            label: 'Debug: View Raw Output Payload',
            children: (
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '12px', maxHeight: '400px', overflow: 'auto' }}>
                {JSON.stringify(run.output_payload, null, 2)}
              </pre>
            ),
          },
          ...(approval?.proposed_changes ? [{
            key: 'debug-approval',
            label: 'Debug: View Approval Proposed Changes',
            children: (
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '12px', maxHeight: '400px', overflow: 'auto' }}>
                {JSON.stringify(approval.proposed_changes, null, 2)}
              </pre>
            ),
          }] : []),
        ]}
      />
    </Modal>
  );
};

